import SellerFollowPopup from '@c/SellerFollowPopup';
import { markSellerFollowPopupSeen } from '@util/firestore/users';
import { useAuth } from 'context/AuthContext';
import { useEffect, useState } from 'react';

interface FirstVisitFollowPopupProps {
  sellerId?: string;
  sellerName?: string;
}

// One global flag (not per-seller): once a person has been shown the popup on
// ANY seller page, it never shows again anywhere. GB #691, Bill's ruling.
const SEEN_KEY = 'gf_seller_follow_popup_seen';

/**
 * Gates the one-time first-visit Follow popup (GB #691). Mount it on any page
 * that carries the Follow CTA (seller storefront + product page). The FIRST such
 * page a person lands on shows the popup once; it is then marked seen and never
 * shown again.
 *
 * "Seen" is persisted two ways, per the approved design:
 *  - localStorage for everyone (reliable, but per-browser — a different device
 *    or cleared storage can show it once more there);
 *  - a user-doc flag for logged-in people, so it follows them across devices;
 *  - reconcile on login: a guest who saw it then signs in gets the doc flag set,
 *    so an identified person won't see it again.
 *
 * All of this runs client-side only (localStorage doesn't exist during SSR), so
 * the popup starts hidden and an effect decides visibility after mount — no
 * hydration mismatch.
 */
const FirstVisitFollowPopup = ({
  sellerId,
  sellerName,
}: FirstVisitFollowPopupProps) => {
  const { user, userDoc, isLoadingUserDoc } = useAuth();
  // Track WHICH seller the popup is open for, not just a boolean. Next.js reuses
  // this component across seller-page navigations (A -> B without unmount), so a
  // bare `visible` would stay true and re-show the one-time nudge on seller B.
  // Rendering only when `visibleFor === sellerId` hides it the moment the seller
  // changes, while leaving it open across the harmless same-seller re-renders
  // (the mark-seen write echoing back through the auth listener).
  const [visibleFor, setVisibleFor] = useState<string | null>(null);

  useEffect(() => {
    // Wait until auth has settled and we actually have a seller to follow.
    if (user === undefined || isLoadingUserDoc) return;
    if (!sellerId || !sellerName) return;
    // Never on the seller's own page.
    if (userDoc && userDoc.uid === sellerId) return;

    if (userDoc) {
      // Logged-in: the account flag is the ONLY source of truth, and it is the
      // only thing we write. We deliberately do not consult or set the
      // browser-global localStorage key for a signed-in person: that key is
      // shared by every account on the device, so reading it would suppress the
      // nudge for someone a previous user already triggered it for, and writing
      // an account flag off it would permanently kill the nudge for a different
      // person who happened to share this browser. Each account therefore sees
      // it exactly once, on any of their devices.
      if (userDoc.seller_follow_popup_seen) return;
      setVisibleFor(sellerId);
      void markSellerFollowPopupSeen(userDoc.uid).catch(() => {});
      return;
    }

    // Guest: a guest is anonymous, so per-browser localStorage is the most we
    // can honestly attribute — "seen once in this browser." We never promote a
    // guest's localStorage flag onto an account (see above); a person who
    // browses as a guest and later signs in may see it once more as themselves,
    // which is the honest limit of not being able to identify an anonymous
    // visitor across the login boundary.
    let seen = false;
    try {
      seen = window.localStorage.getItem(SEEN_KEY) === '1';
    } catch {
      // localStorage can throw (private mode / disabled). Treat as not-seen;
      // the popup is a courtesy, so failing open (showing it) is acceptable.
    }
    if (seen) return;
    setVisibleFor(sellerId);
    try {
      window.localStorage.setItem(SEEN_KEY, '1');
    } catch {
      // No storage → a guest may see it again next visit. Acceptable.
    }
  }, [user, userDoc, isLoadingUserDoc, sellerId, sellerName]);

  if (!sellerId || !sellerName || visibleFor !== sellerId) return null;

  return (
    <SellerFollowPopup
      sellerId={sellerId}
      sellerName={sellerName}
      onDismiss={() => setVisibleFor(null)}
    />
  );
};

export default FirstVisitFollowPopup;
